import {
	ChangeDetectorRef,
	DestroyRef,
	effect,
	inject,
	Pipe,
	PipeTransform,
} from '@angular/core';
import {
	centsToDollars,
	coerceCurrency,
	Currency,
	formatCurrencyAmount,
	isCurrency,
	roundNumber,
} from '@marmon/shared/types/common';
import { LanguageCode, RealLanguageCode } from '@marmon/shared/types/i18n';
import { ANALYSIS_CURRENCY } from '../tokens';

type CentsToDollarsOptions = {
	locale: RealLanguageCode;
	round: boolean;
	currency: Currency;
};

const DefaultCentsToDollarsOptions = {
	round: false,
	locale: LanguageCode.EN,
} as const;

/**
 * Impure so a later currency change on the open analysis refreshes amounts
 * that did not themselves change. Results are cached per binding.
 */
@Pipe({
	name: 'centsToDollars',
	standalone: true,
	pure: false,
})
export class CentsToDollarsPipe implements PipeTransform {
	private readonly activeCurrency = inject(ANALYSIS_CURRENCY, {
		optional: true,
	});
	private readonly cdRef = inject(ChangeDetectorRef, { optional: true });

	private cacheKey = '';
	private cacheValue: string | null = null;

	constructor() {
		const currency = this.activeCurrency;
		const cdRef = this.cdRef;
		if (!currency || !cdRef) return;

		const refresh = effect(() => {
			currency();
			cdRef.markForCheck();
		});
		inject(DestroyRef).onDestroy(() => refresh.destroy());
	}

	transform(cents: number, options?: Partial<CentsToDollarsOptions>) {
		if (cents === undefined) cents = 0;

		const round = options?.round ?? DefaultCentsToDollarsOptions.round;
		const locale = options?.locale ?? DefaultCentsToDollarsOptions.locale;
		const currency = isCurrency(options?.currency)
			? options.currency
			: coerceCurrency(this.activeCurrency?.());

		const key = `${cents}|${round}|${String(locale)}|${currency}`;
		if (key === this.cacheKey) return this.cacheValue;

		const dollars = round
			? roundNumber(centsToDollars(cents), 0)
			: centsToDollars(cents);

		const formatted = formatCurrencyAmount(dollars, currency, {
			round,
			locale: String(locale),
		});
		this.cacheKey = key;
		this.cacheValue = formatted;
		return formatted;
	}
}
